/**
 * useThreadAddPeople — thin React glue for the flag-gated "add people to this
 * thread" affordance (group-threading P2c). Mirrors the panel controller pattern:
 * pure decision logic lives in `src/services/threadAddPeople`; this hook only holds
 * React state and calls the authorized P2a write client. It NEVER writes Firestore
 * directly and NEVER touches the send/composer path.
 *
 * Provably inert when OFF: `resolveThreadAddPeopleEnabled` gates the whole thing;
 * false ⇒ `enabled:false`, `model:null`, and the modal renders nothing.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  deriveAddPeopleModel,
  describeAddParticipantFailure,
  describeRemoveParticipantFailure,
  resolveThreadAddPeopleEnabled,
  runAddParticipant,
} from '../../services/threadAddPeople';
import type {
  AddPeopleModel,
  AddPeoplePendingInvite,
  AddPeopleRosterEntry,
} from '../../services/threadAddPeople';
import { loadWorkspaceRoster } from '../../services/threadAddPeople/workspaceRoster';
import type { ThreadMembershipSource } from '../../services/threadChoicePanel';
import { ADD_PEOPLE_COPY } from './threadAddPeopleCopy';
import {
  addThreadParticipant,
  createThreadMembership,
  removeThreadParticipant,
  type ThreadPostRole,
} from '../../repositories/threadMembershipWriteClient';

export interface UseThreadAddPeopleArgs {
  accountId: string | null | undefined;
  activeThreadId: string | null | undefined;
  selfId: string | null | undefined;
  /** The Track B membership source — read for the thread's current participants. */
  membershipSource?: ThreadMembershipSource;
  /** Canonical owner thread title captured when membership is first created. */
  threadTitle?: string;
  /** Optional testing allowlist override (defaults to the build-time env allowlist). */
  enabledAccountIds?: Iterable<string>;
  /**
   * Called after a SUCCESSFUL add, once the membership source has re-hydrated.
   *
   * Owner feedback 2026-08-07: after adding a person, the thread sat in the
   * wrong sidebar category and the @-mention toggle stayed hidden until the
   * next tab-focus refresh. The add path refreshed the membership SOURCE and
   * this panel's own list, but the host's React state (participant counts,
   * group categorization, summon settings) is rebuilt only by the host's own
   * loader — which nothing here re-triggered. Same class of gap as the toggle
   * feedback loop: a cache refresh is not a state update. The host passes its
   * loader (surfaceSharedThreads) so migration is immediate.
   */
  onMembershipChanged?: () => void | Promise<void>;
  /**
   * Instant-open seed (2026-08-09): the host already read this workspace's
   * roster at mount (sender labels/profiles use the same collection). Passing
   * it here lets the panel paint the member list the moment it opens, while
   * load() revalidates in the background. Staleness is bounded and safe: a
   * member removed since mount is rejected fail-closed by the server on add.
   */
  seedRoster?: AddPeopleRosterEntry[];
}

export interface ThreadAddPeopleController {
  /** True only when the flag resolved ON for this account AND a thread is active. */
  enabled: boolean;
  isOpen: boolean;
  loading: boolean;
  model: AddPeopleModel | null;
  /** The uid currently being added (a row is busy), or null. */
  busyUserId: string | null;
  notice: string | null;
  /**
   * Set when the roster READ failed. Distinct from `notice` (a failed add) and,
   * critically, from an empty model — a failed read must never render as
   * "no one else in this workspace".
   */
  loadError: string | null;
  /**
   * Set when the roster read found the session bound to a DIFFERENT workspace
   * than the one this thread lives in (2026-09-08). The list then shows the
   * thread's workspace only — never a union of the two — and this notice says so.
   */
  mismatchNotice: string | null;
  /** The people filter typed into the panel's search box. */
  filter: string;
  setFilter: (value: string) => void;
  /** The per-thread role a newly-added person will get (#21). */
  addRole: ThreadPostRole;
  setAddRole: (role: ThreadPostRole) => void;
  open: () => void;
  close: () => void;
  /** Re-run the roster read after a failure. */
  retry: () => void;
  addPerson: (userId: string, role?: ThreadPostRole) => void;
  /**
   * Removing a person (2026-09-15). Two-step: `askRemove` opens the confirm on
   * one row (`removeCandidateId`), `removePerson` performs the authorized
   * write, `cancelRemove` closes the ask. The server decides authority; this
   * only sequences the write and reconciles the panel, as `addPerson` does.
   */
  removeCandidateId: string | null;
  askRemove: (userId: string) => void;
  cancelRemove: () => void;
  removePerson: (userId: string) => void;
}

export function useThreadAddPeople(args: UseThreadAddPeopleArgs): ThreadAddPeopleController {
  const accountId = (args.accountId ?? '').trim();
  const threadId = (args.activeThreadId ?? '').trim();
  const selfId = (args.selfId ?? '').trim();
  const { membershipSource, enabledAccountIds, threadTitle } = args;
  /** Ref-read so a new callback identity never re-creates addPerson. */
  const onMembershipChangedRef = useRef(args.onMembershipChanged);
  onMembershipChangedRef.current = args.onMembershipChanged;
  /** Ref-read so a fresh seed array identity never re-creates open(). */
  const seedRosterRef = useRef(args.seedRoster);
  seedRosterRef.current = args.seedRoster;

  const enabled =
    !!threadId &&
    resolveThreadAddPeopleEnabled({ accountId: accountId || null, enabledAccountIds });

  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roster, setRoster] = useState<AddPeopleRosterEntry[]>([]);
  // Synchronous mirror of `roster` for decisions made before React commits
  // (seed-on-open races load()'s settle). Updated at every roster write site.
  const rosterRef = useRef<AddPeopleRosterEntry[]>([]);
  rosterRef.current = roster;
  const [pendingInvites, setPendingInvites] = useState<AddPeoplePendingInvite[]>([]);
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [mismatchNotice, setMismatchNotice] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  // #21: the role the NEXT add will apply. Contributor by default; the panel
  // flips it to 'viewer' for read-only. Resets to contributor on open.
  const [addRole, setAddRole] = useState<ThreadPostRole>('contributor');
  // The row whose remove-confirm is open. One at a time; reset on open/close.
  const [removeCandidateId, setRemoveCandidateId] = useState<string | null>(null);

  // Read the active thread's current participants from the (cached) membership
  // source. Fail-closed to [] so a read failure never widens the addable set.
  const refreshParticipants = useCallback(async () => {
    if (!membershipSource || !threadId) {
      setParticipantIds([]);
      return;
    }
    try {
      const snaps = (await membershipSource.snapshot()) ?? [];
      const snap = snaps.find((s) => s.threadId === threadId);
      setParticipantIds(snap ? [...snap.participantIds] : []);
    } catch {
      setParticipantIds([]);
    }
  }, [membershipSource, threadId]);

  const load = useCallback(async () => {
    if (!enabled || !accountId) return;
    setLoading(true);
    setLoadError(null);
    try {
      // ONE roster source for the whole product: `loadWorkspaceRoster` reconciles
      // the server's `rbacListMembers` (what Team & Roles renders) with the
      // Firestore read of the workspace the thread write path authorizes against,
      // so this picker can no longer contradict the team page the admin just left.
      // It never throws; a total read failure comes back as `error`.
      const [rosterResult] = await Promise.all([
        // Instant-open (2026-08-09): the "Invited" section rides a slower,
        // display-context-only callable — it lands via onPending whenever it
        // settles, never gating the addable list's paint.
        loadWorkspaceRoster(accountId, {
          onPending: (pending) => setPendingInvites(pending),
        }),
        refreshParticipants(),
      ]);

      if (rosterResult.error) {
        // A failed READ must never be dressed up as an empty workspace — that is
        // the exact lie that left an admin staring at "no one else to add" while
        // their team page listed two people. But when rows are ALREADY showing
        // (instant-open seed / earlier read), a background revalidation miss
        // keeps them — the failure wall is for "nothing to show", never for
        // "refresh failed behind a populated list".
        if (rosterRef.current.length === 0) {
          setRoster([]);
          setPendingInvites([]);
          setLoadError(rosterResult.error);
        }
        return;
      }

      const entries = rosterResult.entries.map((entry) => ({
        userId: entry.userId,
        displayName: entry.displayName,
        email: entry.email,
        active: entry.active,
        status: entry.status,
      }));
      rosterRef.current = entries;
      setRoster(entries);
      setMismatchNotice(rosterResult.mismatch ? ADD_PEOPLE_COPY.workspaceMismatch : null);
      // pendingInvites arrives via onPending above — a result-side write here
      // would race the callback and clobber a fresher list with [].
    } catch {
      // Defensive: the loader is contractually non-throwing, but a picker that
      // silently empties itself is precisely the failure mode we're removing.
      if (rosterRef.current.length === 0) {
        setRoster([]);
        setPendingInvites([]);
        setLoadError("Couldn't load your workspace members.");
      }
    } finally {
      setLoading(false);
    }
  }, [enabled, accountId, refreshParticipants]);

  const open = useCallback(() => {
    setNotice(null);
    setLoadError(null);
    setBusyUserId(null);
    setAddRole('contributor');
    setFilter('');
    setRemoveCandidateId(null);
    // Instant-open (2026-08-09): paint the host's mount-time roster right away
    // (first open only — a reopen already keeps its own fresher state), then
    // let load() revalidate against the authoritative reads in the background.
    const seed = seedRosterRef.current;
    if (seed && seed.length > 0 && rosterRef.current.length === 0) {
      rosterRef.current = seed;
      setRoster(seed);
    }
    setIsOpen(true);
    void load();
  }, [load]);

  const close = useCallback(() => {
    setIsOpen(false);
    setBusyUserId(null);
    setNotice(null);
    setLoadError(null);
    setRemoveCandidateId(null);
  }, []);

  const retry = useCallback(() => {
    setNotice(null);
    setLoadError(null);
    void load();
  }, [load]);

  const model = useMemo<AddPeopleModel | null>(() => {
    if (!enabled || !isOpen) return null;
    // Self is always implicitly in the thread even before a membership doc exists,
    // so seed the current set with self when the source has nothing yet.
    const current = participantIds.length ? participantIds : selfId ? [selfId] : [];
    return deriveAddPeopleModel({
      roster,
      currentParticipantIds: current,
      selfId,
      pendingInvites,
    });
  }, [enabled, isOpen, roster, participantIds, selfId, pendingInvites]);

  const addPerson = useCallback(
    (userId: string, role?: ThreadPostRole) => {
      const target = (userId ?? '').trim();
      if (!enabled || !threadId || !target || busyUserId) return;
      const chosenRole: ThreadPostRole = role ?? addRole;
      setBusyUserId(target);
      setNotice(null);
      void runAddParticipant(
        {
          ensureThread: (t) => createThreadMembership(t, undefined, undefined, threadTitle),
          addParticipant: (t, u) => addThreadParticipant(t, u, chosenRole),
        },
        threadId,
        target,
      )
        .then(async (res) => {
          if (res.ok) {
            // Optimistic completion (2026-08-09): the server ACCEPTED the add —
            // reflect it now and free the row, instead of holding the spinner
            // through two more network round-trips. The reads below reconcile.
            // Busy is cleared TARGET-SCOPED: a second add may already be in
            // flight by the time this chain settles, and its row must keep
            // its own busy state.
            setParticipantIds((prev) => (prev.includes(target) ? prev : [...prev, target]));
            setBusyUserId((prev) => (prev === target ? null : prev));
            // Immediate migration (owner feedback 2026-08-07): push the change
            // into the HOST's state — sidebar category, participant count, and
            // the @-mention toggle must all appear now, not on the next
            // tab-focus refresh. The host's loader performs its OWN membership
            // refresh(), so the panel must not fetch one first (the old order
            // paid for the same read twice, serially: panel refresh → host
            // loader drops that cache and refreshes again).
            try {
              await onMembershipChangedRef.current?.();
            } catch {
              /* the periodic/focus refresh reconciles if this pass misses */
            }
            // Reconcile this panel's participant set from the source the host
            // just re-hydrated (warm cache — no extra network)…
            await refreshParticipants();
            // …but never let a stale/failed reconcile hide the ACCEPTED add.
            setParticipantIds((prev) => (prev.includes(target) ? prev : [...prev, target]));
          } else {
            setNotice(describeAddParticipantFailure(res));
          }
        })
        .finally(() => setBusyUserId((prev) => (prev === target ? null : prev)));
    },
    [enabled, threadId, threadTitle, busyUserId, addRole, refreshParticipants],
  );

  const askRemove = useCallback((userId: string) => {
    const target = (userId ?? '').trim();
    if (!target || busyUserId) return;
    setNotice(null);
    setRemoveCandidateId(target);
  }, [busyUserId]);

  const cancelRemove = useCallback(() => setRemoveCandidateId(null), []);

  const removePerson = useCallback(
    (userId: string) => {
      const target = (userId ?? '').trim();
      if (!enabled || !threadId || !target || busyUserId) return;
      setBusyUserId(target);
      setNotice(null);
      setRemoveCandidateId(null);
      void removeThreadParticipant(threadId, target)
        .then(async (res) => {
          if (res.ok) {
            // Same shape as the add: reflect the ACCEPTED removal now, free the
            // row, push the change into the host (sidebar count, summon gate),
            // then reconcile from the re-hydrated source — and never let a
            // stale reconcile put the removed person back in the list.
            setParticipantIds((prev) => prev.filter((id) => id !== target));
            setBusyUserId((prev) => (prev === target ? null : prev));
            try {
              await onMembershipChangedRef.current?.();
            } catch {
              /* the periodic/focus refresh reconciles if this pass misses */
            }
            await refreshParticipants();
            setParticipantIds((prev) => prev.filter((id) => id !== target));
          } else {
            setNotice(describeRemoveParticipantFailure(res.decision));
          }
        })
        .catch(() => {
          setNotice(describeRemoveParticipantFailure('client_network_error'));
        })
        .finally(() => setBusyUserId((prev) => (prev === target ? null : prev)));
    },
    [enabled, threadId, busyUserId, refreshParticipants],
  );

  return {
    enabled,
    isOpen,
    loading,
    model,
    busyUserId,
    notice,
    loadError,
    mismatchNotice,
    filter,
    setFilter,
    addRole,
    setAddRole,
    open,
    close,
    retry,
    addPerson,
    removeCandidateId,
    askRemove,
    cancelRemove,
    removePerson,
  };
}
